'use client';

import { IconAlertCircle } from '@repo/svgs';
import { Button } from '@/components/ui';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';

interface EditApplicationModalProps {
  open: boolean;
  /** Address of the application being edited — named in the question. */
  address: string;
  /** Dismiss without doing anything (X, overlay, Esc). */
  onClose: () => void;
  /** The secondary action — opens the read-only view instead of editing. */
  onView: () => void;
  /**
   * Label for the `onView` button. Defaults to "View application", which is
   * what the applications list needs since the action navigates there. The
   * view page passes "Go back": the user is already on the view, so for them
   * the same button is a dismiss, not a destination.
   */
  viewLabel?: string;
  /** "Continue to edit" — reverts the application to a draft and opens it. */
  onContinue: () => void;
  /** Disables both actions while the revert request is in flight. */
  busy?: boolean;
}

/**
 * Confirmation shown before editing a SUBMITTED application.
 *
 * Editing one is destructive: the backend reverts it to a draft, so the
 * applicant drops out of consideration until they resubmit. That is not
 * obvious from an "Edit" link, hence the interstitial.
 *
 * Deliberately NOT shown for drafts — per the Figma annotation, a draft's
 * Edit link opens the editor directly. The warning would also be untrue
 * there, since a draft is already a draft.
 *
 * Uses the shared ResponsiveModal, so this is a centered dialog on desktop
 * and a bottom sheet on mobile, with the AppBar close button for free.
 */
export function EditApplicationModal({
  open,
  address,
  onClose,
  onView,
  onContinue,
  busy = false,
  viewLabel = 'View application',
}: EditApplicationModalProps) {
  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      label="Edit application"
      maxWidth="md"
      /*
       * The design insets content 24px from the modal edge: 16px on the
       * content column plus 8px on each block below. The wrapper's stock
       * `px-6` made that 32px, so every row sat 8px right of the app bar
       * title, which uses the AppBar's own pl-6.
       *
       * Vertically the design is 20px above the icon and 24px below the
       * buttons (its 20px content padding plus the shell's 4px). The stock
       * `py-4` was adding 16px to both ends.
       */
      contentPaddingClassName="px-4 pt-5 pb-6"
      // Shell radius is 32px in the design, not the Modal's stock 16px.
      // The inner body has to match or its corners clip inside the outer
      // shell and paint a double rim along the bottom edge.
      contentClassName="rounded-[32px]"
      desktopBodyClassName="rounded-[32px]"
    >
      <div className="flex flex-col gap-4">
        <div className="px-2">
          <IconAlertCircle className="h-8 w-8 icon-caution" aria-hidden />
        </div>

        <div className="flex flex-col gap-2 px-2 text-primary">
          <p className="text-heading-6">Edit your application for {address}?</p>
          <p className="text-body-2">
            If you continue, your application will become a draft, even if you don&apos;t change
            anything. You&apos;ll need to resubmit to be considered for this apartment.
          </p>
        </div>

        {/*
         * Mobile stacks the actions with the primary on top, per the mweb
         * sheet design; desktop keeps them side by side, tertiary first.
         *
         * `flex-col-reverse` rather than reordering the JSX: it keeps DOM
         * order matching the desktop row's visual order, so keyboard focus
         * runs left-to-right there. The cost is that the mobile sheet's tab
         * order is bottom-to-top — acceptable on a touch surface, and the
         * alternative just moves the same mismatch onto desktop, where
         * keyboard use is far more common.
         *
         * `md:` is the 768px breakpoint ResponsiveModal itself switches on,
         * so the direction flip lands exactly when the sheet becomes a modal.
         */}
        <div className="flex flex-col-reverse md:flex-row items-start justify-center gap-4 px-2">
          <Button variant="tertiary" size="md" fullWidth disabled={busy} onClick={onView}>
            {viewLabel}
          </Button>
          <Button variant="primary" size="md" fullWidth disabled={busy} onClick={onContinue}>
            Continue to edit
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
